/**
 * <s-withdrawal-form> — client-side checks for the withdrawal (Widerruf) form.
 * Native `required` validation stays the baseline; this component adds the
 * conditional rules the browser can't express:
 *   - "Betroffene Artikel" is required only for a partial withdrawal
 *   - "Erhalten am" is optional when the buyer has not received the goods yet
 *   - delivery date can't be before the order date
 * It also stamps the buyer's local submit time into a hidden field so the
 * copy sent to the store records it.
 */
class SWithdrawalForm extends HTMLElement {
  #ac;

  connectedCallback() {
    const form = this.querySelector('form');
    if (!form) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const items = form.querySelector('[data-withdrawal-items]');
    const itemsMarker = form.querySelector('[data-withdrawal-items-required]');
    const received = form.querySelector('[data-withdrawal-received]');
    const notReceived = form.querySelector('[data-withdrawal-not-received]');
    const orderDate = form.querySelector('[data-withdrawal-order-date]');
    const timestamp = form.querySelector('[data-withdrawal-timestamp]');

    const syncScope = () => {
      const partial = form.querySelector('[data-withdrawal-scope]:checked')?.hasAttribute('data-partial') ?? false;
      if (!items) return;
      items.required = partial;
      items.setAttribute('aria-required', String(partial));
      if (itemsMarker) itemsMarker.hidden = !partial;
    };

    const syncReceived = () => {
      if (!received || !notReceived) return;
      const pending = notReceived.checked;
      received.required = !pending;
      received.disabled = pending;
      received.setAttribute('aria-required', String(!pending));
      if (pending) received.value = '';
    };

    const checkDates = () => {
      if (!received || !orderDate) return;
      const message = this.dataset.dateOrderMessage || '';
      const invalid = received.value && orderDate.value && received.value < orderDate.value;
      received.setCustomValidity(invalid ? message : '');
    };

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches('[data-withdrawal-scope]')) syncScope();
      if (target === notReceived) syncReceived();
      if (target === received || target === orderDate) checkDates();
    }, opts);

    form.addEventListener('submit', (event) => {
      checkDates();
      if (!form.checkValidity()) {
        event.preventDefault();
        form.reportValidity();
        form.querySelector(':invalid')?.focus();
        return;
      }
      if (timestamp) {
        const now = new Date();
        timestamp.value = `${now.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'medium' })} (${now.toISOString()})`;
      }
    }, opts);

    syncScope();
    syncReceived();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-withdrawal-form')) {
  customElements.define('s-withdrawal-form', SWithdrawalForm);
}
